import { CANONICAL_FEED_CATEGORIES } from '../../constants/feedCategories';
import { parseApiDate, parseApiDateMs } from '../../utils/parseApiDate';

export const DESK_COLUMNS = [
  {
    id: 'markets',
    label: 'Markets & Macro',
    shortLabel: 'Markets',
    icon: '📈',
    accentVar: '--desk-accent-markets',
    accentDimVar: '--desk-accent-markets-dim',
    feedCategory: 'business,finance',
  },
  {
    id: 'geopolitics',
    label: 'Geopolitics & Events',
    shortLabel: 'Geopolitics',
    icon: '🌍',
    accentVar: '--desk-accent-geopolitics',
    accentDimVar: '--desk-accent-geopolitics-dim',
    feedCategory: 'politics,news',
  },
  {
    id: 'techscience',
    label: 'Tech · Science · Health',
    shortLabel: 'Tech & Health',
    icon: '🔬',
    accentVar: '--desk-accent-techscience',
    accentDimVar: '--desk-accent-techscience-dim',
    feedCategory: 'technology,science,health',
  },
];

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * Visual accent wash fades to column black over 1h (same window as NEW badge).
 * Rank API `w` still uses the Desk days window; this is display-only.
 */
export const DESK_VISUAL_FADE_MAX_MS = HOUR_MS;

/** Max solid-accent mix at full freshness (accent-dim is too faint to read). */
export const DESK_VISUAL_ACCENT_MIX_MAX = 0.32;

export const DESK_CATEGORY_BY_ID = Object.fromEntries(
  DESK_COLUMNS.map((column) => [column.id, column]),
);

/**
 * Temporary category → column accent palette (theme-independent).
 * Complex defaults reuse slot CSS vars via resolveDeskColumnAccent.
 */
export const DESK_SIMPLE_CATEGORY_ACCENTS = {
  business: { accent: '#f0b429', accentDim: 'rgba(240, 180, 41, 0.15)' },
  finance: { accent: '#d4a017', accentDim: 'rgba(212, 160, 23, 0.15)' },
  politics: { accent: '#e85d5d', accentDim: 'rgba(232, 93, 93, 0.15)' },
  news: { accent: '#c44b4b', accentDim: 'rgba(196, 75, 75, 0.15)' },
  technology: { accent: '#3dd6c3', accentDim: 'rgba(61, 214, 195, 0.15)' },
  science: { accent: '#6ea8fe', accentDim: 'rgba(110, 168, 254, 0.15)' },
  health: { accent: '#5ecf8a', accentDim: 'rgba(94, 207, 138, 0.15)' },
  environment: { accent: '#7cb342', accentDim: 'rgba(124, 179, 66, 0.15)' },
  investigative: { accent: '#c77dff', accentDim: 'rgba(199, 125, 255, 0.15)' },
  education: { accent: '#ff9f43', accentDim: 'rgba(255, 159, 67, 0.15)' },
  justice: { accent: '#a78bfa', accentDim: 'rgba(167, 139, 250, 0.15)' },
  general: { accent: '#94a3b8', accentDim: 'rgba(148, 163, 184, 0.15)' },
};

/** Title-case a simple canonical category slug for the column header. */
export function formatDeskCategoryLabel(slug) {
  const raw = String(slug || '').trim();
  if (!raw) {
    return '';
  }
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** Complex defaults (OR-lists) first, then simple canonical categories. */
export const DESK_COMPLEX_CATEGORY_PRESETS = DESK_COLUMNS.map((column) => ({
  id: `complex:${column.id}`,
  kind: 'complex',
  label: column.label,
  feedCategory: column.feedCategory,
  columnId: column.id,
}));

export const DESK_SIMPLE_CATEGORY_PRESETS = CANONICAL_FEED_CATEGORIES.map((slug) => ({
  id: `simple:${slug}`,
  kind: 'simple',
  label: formatDeskCategoryLabel(slug),
  feedCategory: slug,
}));

export const DESK_CATEGORY_PRESETS = [
  ...DESK_COMPLEX_CATEGORY_PRESETS,
  ...DESK_SIMPLE_CATEGORY_PRESETS,
];

export function findDeskCategoryPreset(presetId) {
  return DESK_CATEGORY_PRESETS.find((preset) => preset.id === presetId) || null;
}

export function findDeskCategoryPresetByFeedCategory(feedCategory) {
  const match = String(feedCategory || '').trim();
  if (!match) {
    return null;
  }
  return DESK_CATEGORY_PRESETS.find((preset) => preset.feedCategory === match) || null;
}

/** True when the slot is using its configured default OR-list (no user override). */
export function usesDefaultDeskFeedCategory(column, override) {
  if (!override || !override.feedCategory) {
    return true;
  }
  return override.feedCategory === column.feedCategory;
}

/**
 * Resolve accent for an effective feedCategory.
 * Complex OR-lists → theme CSS vars; simple slugs → temporary hex palette.
 */
export function resolveDeskColumnAccent(feedCategory, fallbackColumn) {
  const match = String(feedCategory || '').trim();
  const complex = DESK_COLUMNS.find((column) => column.feedCategory === match);
  if (complex) {
    return {
      accentVar: complex.accentVar,
      accentDimVar: complex.accentDimVar,
      accentColor: null,
      accentDimColor: null,
    };
  }
  const simple = DESK_SIMPLE_CATEGORY_ACCENTS[match];
  if (simple) {
    return {
      accentVar: fallbackColumn?.accentVar || '--desk-accent-markets',
      accentDimVar: fallbackColumn?.accentDimVar || '--desk-accent-markets-dim',
      accentColor: simple.accent,
      accentDimColor: simple.accentDim,
    };
  }
  return {
    accentVar: fallbackColumn?.accentVar || '--desk-accent-markets',
    accentDimVar: fallbackColumn?.accentDimVar || '--desk-accent-markets-dim',
    accentColor: null,
    accentDimColor: null,
  };
}

/**
 * Slot id stays fixed; label + feedCategory + accents follow the override when set.
 */
export function resolveEffectiveDeskColumn(column, override) {
  if (usesDefaultDeskFeedCategory(column, override)) {
    return column;
  }
  const preset = override.presetId
    ? findDeskCategoryPreset(override.presetId)
    : findDeskCategoryPresetByFeedCategory(override.feedCategory);
  const label = override.label || preset?.label || formatDeskCategoryLabel(override.feedCategory);
  const accent = resolveDeskColumnAccent(override.feedCategory, column);
  return {
    ...column,
    label,
    shortLabel: label,
    feedCategory: override.feedCategory,
    accentVar: accent.accentVar,
    accentDimVar: accent.accentDimVar,
    accentColor: accent.accentColor,
    accentDimColor: accent.accentDimColor,
  };
}

/** Articles published within this window get the NEW badge (display-only; color fade is 1h). */
export const DESK_NEW_WINDOW_MS = 15 * 60 * 1000;

/** Relative age from UTC instant; labels are timezone-agnostic. */
export function formatDeskTime(publishedAt) {
  const date = parseApiDate(publishedAt);
  if (!date) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  return `${days}d`;
}

/** Buckets by UTC age (1h / 24h). */
export function bucketArticles(articles) {
  const now = Date.now();
  const hourMs = 60 * 60 * 1000;
  const dayMs = 24 * hourMs;
  const buckets = { hour: [], today: [], older: [] };

  articles.forEach((article) => {
    const t = parseApiDateMs(article.publishedAt);
    if (Number.isNaN(t)) {
      buckets.older.push(article);
      return;
    }
    const age = now - t;
    if (age <= hourMs) buckets.hour.push(article);
    else if (age <= dayMs) buckets.today.push(article);
    else buckets.older.push(article);
  });

  return buckets;
}

/** NEW badge: published within windowMs of now (UTC-based Instant). Default = 15 minutes. */
export function isDeskArticleNew(publishedAt, windowMs = DESK_NEW_WINDOW_MS) {
  const t = parseApiDateMs(publishedAt);
  if (Number.isNaN(t)) return false;
  return Date.now() - t <= windowMs;
}

export function countDeskNewArticles(articles, windowMs = DESK_NEW_WINDOW_MS) {
  return articles.filter((article) => isDeskArticleNew(article.publishedAt, windowMs)).length;
}

/** Clamp API window signal `w` to [0, 1]; missing/invalid → 0. */
export function clampDeskW(w) {
  const n = Number(w);
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

/**
 * Display wash from age (not raw API `w`).
 * Uses the same linear decay as rank `w`, but caps the fade span at 1h
 * (matches NEW badge): full accent when fresh → column black after 1 hour.
 */
export function deskVisualW({ publishedAt, w, windowDays = 7 } = {}) {
  const rankWindowMs = Math.max(DAY_MS / 24, (Number(windowDays) || 7) * DAY_MS);
  const visualMs = Math.min(rankWindowMs, DESK_VISUAL_FADE_MAX_MS);
  const publishedMs = parseApiDateMs(publishedAt);
  if (!Number.isNaN(publishedMs) && visualMs > 0) {
    const ageMs = Math.max(0, Date.now() - publishedMs);
    return clampDeskW(1 - ageMs / visualMs);
  }
  return clampDeskW(w);
}

/** Inline CSS vars: solid accent mix % from visual wash. */
export function deskWindowStyle(articleOrW, windowDays = 7) {
  const visual =
    articleOrW != null && typeof articleOrW === 'object'
      ? deskVisualW({
          publishedAt: articleOrW.publishedAt,
          w: articleOrW.w,
          windowDays,
        })
      : clampDeskW(articleOrW);
  const mixPct = (visual * DESK_VISUAL_ACCENT_MIX_MAX * 100).toFixed(2);
  return {
    '--desk-w': String(visual),
    '--desk-w-mix': `${mixPct}%`,
  };
}
